import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import { ArrowRight, KeyRound, LockKeyhole, MessagesSquare, ShieldCheck, UserCheck, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoginArea } from "@/components/auth/LoginArea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChama } from "@/hooks/useChama";
import { useTheme } from "@/hooks/useTheme";
import { Moon, Sun } from "lucide-react";
import { ChamaWordmark, ChamaLogo } from "@/components/chama/ChamaLogo";
import { BeadDivider, ShukaPattern } from "@/components/chama/BeadDivider";
import { ApplicationForm } from "@/components/chama/ApplicationForm";
import {
  DeclinedCard,
  InvitedCard,
  LoadingGate,
  PendingCard,
  ReviewNote,
  UnsupportedCard,
} from "@/components/chama/GateCards";
import { APP_NAME } from "@/lib/concord/config";

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Toggle theme"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
    >
      {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </Button>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t py-8">
      <div className="container flex flex-col items-center gap-3 text-center text-sm text-muted-foreground">
        <BeadDivider />
        <p>
          Open protocol (Concord / Nostr) · End-to-end encrypted · Works with{" "}
          <a
            href="https://armada.buzz"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Armada
          </a>
        </p>
        <p>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Vibed with Shakespeare
          </a>
        </p>
      </div>
    </footer>
  );
}

const FEATURES = [
  {
    icon: LockKeyhole,
    title: "End-to-end encrypted",
    body: "Every message rides a sealed Concord stream. Relays — and anyone watching — see only noise.",
  },
  {
    icon: UserCheck,
    title: "Moderated membership",
    body: "You apply with your Nostr key; the founder reviews each request by hand. No bots, no drive-bys.",
  },
  {
    icon: MessagesSquare,
    title: "Open & portable",
    body: "Built on the Concord protocol, the same one Armada speaks. Your community isn't locked to this app.",
  },
  {
    icon: KeyRound,
    title: "Your keys, your voice",
    body: "No passwords, no platform account. Your Nostr key is your identity and your invite.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Apply with your Nostr key",
    body: "Tell us who you are and why you want in. It goes to the moderator end-to-end encrypted.",
  },
  {
    n: "2",
    title: "The founder reviews it",
    body: "A human reads every application. This keeps the chama safe, focused, and worth joining.",
  },
  {
    n: "3",
    title: "Accept your invite & chat",
    body: "Approval arrives as a Concord invite. Accept it here or in Armada — same community, same keys.",
  },
];

const Index = () => {
  useSeoMeta({
    title: `${APP_NAME} — Kenyan women building Bitcoin & Nostr`,
    description:
      "A private, end-to-end encrypted chama for Kenya-based women building open-source Bitcoin and Nostr software. Moderated membership, open protocol.",
  });

  const { user } = useCurrentUser();
  const chama = useChama();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (chama.status === "member") navigate("/chat");
  }, [chama.status, navigate]);

  const showForm =
    chama.status === "stranger" ||
    (chama.status === "pending" && editing) ||
    (chama.status === "declined" && editing);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" aria-label="Chama Chat home">
            <ChamaWordmark />
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LoginArea className="max-w-48" />
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="relative isolate overflow-hidden border-b">
          <div className="container grid gap-10 py-16 md:grid-cols-2 md:items-center md:py-24">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
                <Zap className="size-3.5 text-gold" />
                Concord protocol · Armada-compatible
              </div>
              <h1 className="font-display text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
                A chama for Kenya&rsquo;s women building{" "}
                <span className="text-primary">Bitcoin</span> &amp;{" "}
                <span className="text-accent">Nostr</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-prose">
                A <em>chama</em> is how Kenyan women have always pooled
                strength. This one pools code — a private, end-to-end
                encrypted circle for the women shipping open-source Bitcoin
                and Nostr software in Kenya.
              </p>
              {chama.status === "logged-out" && (
                <div className="flex flex-wrap items-center gap-4">
                  <LoginArea className="max-w-48" />
                  <span className="text-sm text-muted-foreground">
                    Join with your Nostr key to apply
                  </span>
                </div>
              )}
            </div>
            <div className="relative">
              <div className="overflow-hidden rounded-2xl border shadow-lg">
                <ShukaPattern className="aspect-[16/10] w-full" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 rounded-xl border bg-card p-4 shadow-md">
                <ChamaLogo className="size-10" />
                <div>
                  <p className="font-display font-semibold leading-tight">The circle holds</p>
                  <p className="text-xs text-muted-foreground">Sealed with Concord streams</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gate */}
        {user && chama.status !== "member" && (
          <section className="container py-12 max-w-2xl">
            {chama.status === "loading" && <LoadingGate />}
            {chama.status === "unsupported" && <UnsupportedCard />}
            {chama.isModerator && chama.status !== "loading" && chama.status !== "unsupported" && (
              <Card className="shadow-md border-primary/20">
                <CardContent className="py-10 px-8 text-center space-y-4">
                  <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
                    <ShieldCheck className="size-6 text-primary" />
                  </div>
                  <h2 className="font-display text-2xl font-semibold">
                    Habari, founder
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    You&rsquo;re the moderator. Found the chama and start
                    reviewing applications from your moderation dashboard.
                  </p>
                  <Button size="lg" asChild>
                    <Link to="/moderate">
                      Open moderation
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
            {!chama.isModerator && showForm && (
              <ApplicationForm
                initial={editing ? chama.application?.request ?? null : null}
                onSubmitted={(record) => {
                  chama.setApplication(record);
                  setEditing(false);
                }}
              />
            )}
            {!chama.isModerator && chama.status === "pending" && !editing && chama.application && (
              <PendingCard application={chama.application} onReapply={() => setEditing(true)} />
            )}
            {!chama.isModerator && chama.status === "invited" && chama.invite && (
              <InvitedCard
                invite={chama.invite}
                membership={chama.membership}
                communityList={chama.communityList}
                onAccepted={chama.refetchAll}
              />
            )}
            {!chama.isModerator && chama.status === "declined" && !editing && chama.decline && (
              <DeclinedCard decline={chama.decline} onReapply={() => setEditing(true)} />
            )}
            {!chama.isModerator && (chama.status === "stranger" || chama.status === "pending") && (
              <div className="mt-4">
                <ReviewNote />
              </div>
            )}
          </section>
        )}

        {/* Features */}
        <section className="container py-16">
          <div className="text-center space-y-3 mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Harambee, but for sats and signatures
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              No company in the middle. Just a shared key, a signed roster,
              and a circle of women who ship.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <Card key={f.title} className="shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                    <f.icon className="size-5 text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="border-t bg-secondary/40 py-16">
          <div className="container">
            <div className="text-center space-y-3 mb-10">
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                How joining works
              </h2>
              <p className="text-muted-foreground">
                Membership is by application, reviewed by the founder.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3 max-w-4xl mx-auto">
              {STEPS.map((s) => (
                <Card key={s.n} className="shadow-sm">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-display font-semibold">
                      {s.n}
                    </div>
                    <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {chama.status === "logged-out" && (
              <div className="mt-10 flex justify-center">
                <LoginArea className="max-w-60" />
              </div>
            )}
            {!chama.isModerator && chama.status === "stranger" && (
              <div className="mt-10 flex justify-center">
                <Button
                  size="lg"
                  onClick={() =>
                    document
                      .getElementById("apply-about")
                      ?.scrollIntoView({ behavior: "smooth", block: "center" })
                  }
                >
                  Start your application
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Moderator note */}
        <section className="container py-16">
          <div className="mx-auto max-w-2xl rounded-2xl border bg-card p-8 shadow-sm flex flex-col md:flex-row items-center gap-6">
            <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-accent/10">
              <ShieldCheck className="size-7 text-accent" />
            </div>
            <div className="space-y-2 text-center md:text-left">
              <h2 className="font-display text-xl font-semibold">
                Kept by a person, not an algorithm
              </h2>
              <p className="text-sm text-muted-foreground">
                The chama is founded and moderated by MK Fain. She reads every
                application, approves every invite, and holds the banhammer.
                Already in another Concord client? Your membership syncs —
                this community is the same one you&rsquo;ll see in Armada.
              </p>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Index;
